package com.arena_web.ui.components.widgets

import androidx.compose.runtime.Composable
import com.arena_web.ui.theme.ThemeController
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// Inline sun/moon marks — hand-drawn SVG so they stay crisp and can be
// recolored per state (emoji rendering was blurry and platform-dependent).
private const val SUN_SVG =
    "<svg viewBox='0 0 24 24' width='100%' height='100%' fill='none' " +
        "stroke='#F5A524' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>" +
        "<circle cx='12' cy='12' r='4.2' fill='#FFC94D' stroke='#F5A524'/>" +
        "<path d='M12 2.6v2.4M12 19v2.4M21.4 12H19M5 12H2.6" +
        "M18.7 5.3l-1.7 1.7M7 17l-1.7 1.7M18.7 18.7L17 17M7 7L5.3 5.3'/></svg>"

private const val MOON_SVG =
    "<svg viewBox='0 0 24 24' width='100%' height='100%' fill='none'>" +
        "<path d='M20 14.5A8 8 0 0 1 9.5 4a0.6 0.6 0 0 0-0.8-0.8 8.8 8.8 0 1 0 12.1 12.1 " +
        "0.6 0.6 0 0 0-0.8-0.8z' fill='#EAF0FF' stroke='#C3D3F5' stroke-width='0.8'/>" +
        "<circle cx='13' cy='8.5' r='0.7' fill='#C3D3F5'/>" +
        "<circle cx='16' cy='12' r='0.5' fill='#C3D3F5'/>" +
        "<circle cx='11.5' cy='12' r='0.45' fill='#C3D3F5'/></svg>"

/**
 * Dark/light theme switch backed by ThemeController (CSS-token theming —
 * NOT Silk's ColorMode, which stays locked to DARK).
 *
 * A pill track that gradients between a deep night-navy (dark active) and a
 * warm sky-blue (light active); a glowing knob slides across and swaps a
 * hand-drawn sun ⇄ moon. Decorative stars fade in on the dark side.
 */
@Composable
fun ThemeToggle(
    modifier: Modifier = Modifier
) {
    val isLight = ThemeController.isLight

    Box(
        modifier = modifier
            .width(56.px)
            .height(30.px)
            .borderRadius(15.px)
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .styleModifier {
                property("overflow", "hidden")
                property(
                    "background",
                    if (isLight)
                        "linear-gradient(135deg, #4DA6FF 0%, #7FC4FF 100%)"
                    else
                        "linear-gradient(135deg, #0B1430 0%, #1C2B54 100%)"
                )
                property("border", "1px solid ${if (isLight) "rgba(255,255,255,0.55)" else "rgba(120,150,220,0.35)"}")
                property(
                    "box-shadow",
                    if (isLight)
                        "inset 0 1px 2px rgba(255,255,255,0.5), 0 2px 6px rgba(0,97,255,0.30)"
                    else
                        "inset 0 1px 2px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.35)"
                )
                property("transition", "background 350ms ease, border-color 350ms ease, box-shadow 350ms ease")
            }
            .onClick { ThemeController.toggle() }
    ) {
        // Decorative stars — visible only on the dark (moon) side.
        Div(attrs = Modifier.styleModifier {
            property("position", "absolute")
            property("inset", "0")
            property("pointer-events", "none")
            property("opacity", if (isLight) "0" else "1")
            property("transition", "opacity 350ms ease")
            property(
                "background-image",
                "radial-gradient(1px 1px at 10px 8px, rgba(255,255,255,0.9) 50%, transparent 51%), " +
                    "radial-gradient(1px 1px at 20px 20px, rgba(255,255,255,0.7) 50%, transparent 51%), " +
                    "radial-gradient(0.8px 0.8px at 15px 15px, rgba(255,255,255,0.6) 50%, transparent 51%)"
            )
        }.toAttrs())

        // Sliding knob with the sun/moon glyph.
        Div(attrs = Modifier.styleModifier {
            property("position", "absolute")
            property("top", "3px")
            property("left", if (isLight) "29px" else "3px")
            property("width", "24px")
            property("height", "24px")
            property("border-radius", "50%")
            property(
                "background",
                if (isLight)
                    "radial-gradient(circle at 35% 30%, #FFFFFF, #FFF3D6)"
                else
                    "radial-gradient(circle at 35% 30%, #FFFFFF, #DCE6FB)"
            )
            property(
                "box-shadow",
                if (isLight)
                    "0 0 8px rgba(255,201,77,0.75), 0 1px 3px rgba(0,0,0,0.25)"
                else
                    "0 0 6px rgba(180,205,255,0.55), 0 1px 3px rgba(0,0,0,0.35)"
            )
            property("transition", "left 350ms cubic-bezier(0.4, 0.0, 0.2, 1), background 350ms ease, box-shadow 350ms ease")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
        }.toAttrs()) {
            // key(isLight): the ref that injects the SVG fires once at mount —
            // re-key so it remounts (and swaps sun⇄moon) on every theme flip.
            androidx.compose.runtime.key(isLight) {
                Div(attrs = Modifier.styleModifier {
                    property("width", "15px")
                    property("height", "15px")
                    property("line-height", "0")
                }.toAttrs {
                    ref { el ->
                        el.innerHTML = if (isLight) SUN_SVG else MOON_SVG
                        onDispose { }
                    }
                })
            }
        }
    }
}
